import { Helmet } from "react-helmet-async";
import { ArrowLeft, Shield, Lock, Eye, Trash2, Bell, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyPhone from "@/components/StickyPhone";

const Section = ({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) => (
  <div className="mb-10">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-xl font-heading font-bold text-foreground">{title}</h2>
    </div>
    <div className="text-muted-foreground space-y-3 pl-12">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy | Hill Brothers</title>
        <meta name="description" content="Learn how Hill Brothers Painting &amp; Contracting protects your privacy. AES-256-GCM encryption, no data selling, and full transparency." />
        <meta property="og:title" content="Privacy Policy | Hill Brothers" />
        <meta property="og:description" content="Learn how we protect your privacy with industry-standard encryption and transparent data practices." />
        <meta property="og:url" content="https://hillbrothersnh.com/privacy" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Privacy Policy | Hill Brothers" />
        <meta name="twitter:description" content="How we protect your privacy and your data." />
        <link rel="canonical" href="https://hillbrothersnh.com/privacy" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"BreadcrumbList",
          itemListElement:[
            {"@type":"ListItem",position:1,name:"Home",item:"https://hillbrothersnh.com/"},
            {"@type":"ListItem",position:2,name:"Privacy Policy",item:"https://hillbrothersnh.com/privacy"}
          ]
        })}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        <section className="bg-primary py-16">
          <div className="section-container">
            <Link to="/" className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-2xl">
              Your privacy matters to us. Here's exactly what we collect, why, and how we protect it, in plain English.
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="section-container max-w-3xl">

            {/* Encryption callout */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-12 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-foreground mb-1">Industry-Standard Encryption</h3>
                <p className="text-muted-foreground text-sm">
                  All sensitive data submitted through our forms, including your name, email, phone number, address, and messages, is encrypted at rest using <strong className="text-foreground">AES-256-GCM</strong>, the same encryption standard used by banks and government agencies. Your information is never stored in plain text.
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-10">
              <strong>Effective Date:</strong> February 2026 &nbsp;|&nbsp; <strong>Last Updated:</strong> February 18, 2026
            </p>

            <Section icon={Eye} title="What We Collect & Why">
              <p><strong className="text-foreground">Contact form:</strong> Your name, email, phone number (optional), project address (optional), preferred appointment date (optional), and message. We use this solely to respond to your inquiry and schedule estimates.</p>
              <p><strong className="text-foreground">Job applications:</strong> Your name, email, phone, work experience, qualifications, resume, and work samples. We use this only to evaluate your application for employment or contract work.</p>
              <p><strong className="text-foreground">Website analytics:</strong> Our website collects basic, anonymous page-view data (which pages are visited and general traffic source) to help us improve the site. This data contains no personally identifiable information and is stored in our own secure database, not shared with any third party. We do not use advertising cookies, social media tracking pixels, or third-party analytics platforms.</p>
            </Section>

            <Section icon={Shield} title="How We Protect Your Data">
              <p>All form submissions are encrypted at rest using AES-256-GCM encryption before being stored. Database columns containing personal information are replaced with redacted placeholders. The actual data lives only in the encrypted bundle.</p>
              <p>Decryption happens on-demand and is restricted to authorized administrators only. We are actively and continuously improving our security systems to ensure the highest level of protection for your information.</p>
              <p>We take the privacy, security, and well-being of every person who interacts with our site very seriously.</p>
            </Section>

            <Section icon={Lock} title="We Never Sell or Share Your Data">
              <p>We will <strong className="text-foreground">never</strong> sell, rent, trade, or share your personal information with third parties for marketing purposes. Period.</p>
              <p>Your data is used exclusively to respond to your inquiry or process your job application. The only third-party service that touches your data is our email delivery provider, which is used solely to send you a response or notify our team of your submission.</p>
            </Section>

            <Section icon={Bell} title="Communications & Consent">
              <p>We will never send you marketing emails or promotional materials without your explicit consent.</p>
              <p>When you submit a contact form, you'll have the option to consent to us reaching out by phone or text. If you don't check that box, we'll only respond via email.</p>
              <p>Every email we send includes an unsubscribe link. Any recipient can opt out at any time.</p>
            </Section>

            <Section icon={Mail} title="How to Unsubscribe">
              <p>Every email we send includes an unsubscribe link at the bottom. Click it, and you're done. No hoops, no delays.</p>
              <p>You can also contact us directly at <a href="tel:+16034244608" className="text-primary underline hover:no-underline font-medium">603 424 4608</a> to request removal from any communications.</p>
            </Section>

            <Section icon={Trash2} title="Data Deletion">
              <p>You have the right to request deletion of any personal data we hold about you. Simply call us at <a href="tel:+16034244608" className="text-primary underline hover:no-underline font-medium">603 424 4608</a> or email us, and we'll remove your data promptly.</p>
              <p>We retain contact form submissions and job applications only as long as they're needed for their original purpose. Once a project inquiry is resolved or a hiring decision is made, data is subject to deletion.</p>
            </Section>

            <div className="border-t border-border pt-8 mt-12">
              <h2 className="text-xl font-heading font-bold text-foreground mb-3">Cookies & Third-Party Services</h2>
              <p className="text-muted-foreground mb-3">
                Our website collects basic, anonymous page-view data (which pages are visited) to help us improve the site. This data contains no personally identifiable information. We do not use advertising cookies, social media tracking pixels, or third-party analytics platforms that collect personal information.
              </p>
              <p className="text-muted-foreground">
                If this policy changes in any material way, we'll update this page with a new effective date. We encourage you to check back periodically.
              </p>
            </div>

            <div className="border-t border-border pt-8 mt-8">
              <h2 className="text-xl font-heading font-bold text-foreground mb-3">Questions?</h2>
              <p className="text-muted-foreground">
                If you have any questions about this policy or how your data is handled, please call us at{" "}
                <a href="tel:+16034244608" className="text-primary underline hover:no-underline font-medium">603 424 4608</a> or{" "}
                <Link to="/#contact" className="text-primary underline hover:no-underline font-medium">send us a message</Link>.
              </p>
            </div>

          </div>
        </section>
      </main>

      <Footer />
      <StickyPhone />
    </div>
  );
}
